import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight, ChevronDown, Sparkles } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { fetchServicePages } from "@/lib/api";
import Logo from "./Logo";

const NAV_AFTER = [
  { to: "/over-mij", label: "Over mij" },
  { to: "/cases", label: "Cases" },
  { to: "/blog", label: "Blog" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  const { data: servicePages = [] } = useQuery({ queryKey: ["service-pages"], queryFn: fetchServicePages });
  const menuPages = servicePages.map((p) => ({ slug: p.slug, menu_label: p.nav_label || p.title, title: p.title }));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const linkCls = ({ isActive }) =>
    `link-slide transition-colors ${isActive ? "text-bone" : "text-bone-muted hover:text-bone"}`;

  return (
    <>
      <header
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-500 ${
          scrolled ? "backdrop-blur-xl bg-ink/70 border-b border-black/5" : "bg-transparent"
        }`}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16 py-5 flex items-center justify-between">
          <Logo size="md" />
          <nav className="hidden md:flex items-center gap-10 font-sans text-sm">
            <NavLink to="/" end className={linkCls} data-testid="nav-home">
              Home
            </NavLink>
            <DienstenDropdown menuPages={menuPages} pathname={location.pathname} />
            {NAV_AFTER.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                className={linkCls}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
            <NavLink
              to="/digitaliseringsscan"
              className={({ isActive }) =>
                `flex items-center gap-1.5 font-medium transition-colors ${
                  isActive ? "text-bell" : "text-bell/80 hover:text-bell"
                }`
              }
              data-testid="nav-ai-scan"
            >
              <Sparkles className="h-3.5 w-3.5" />
              AI-scan
            </NavLink>
          </nav>
          <div className="flex items-center gap-3">
            <NavLink
              to="/contact"
              className="hidden md:inline-flex items-center gap-2 rounded-full bg-bone text-ink px-5 py-2.5 text-sm font-medium hover:bg-ember hover:text-white transition-colors"
              data-testid="nav-contact-cta"
            >
              Contact
              <ArrowUpRight className="h-4 w-4" />
            </NavLink>
            <button
              className="md:hidden p-2 -mr-2 text-bone"
              onClick={() => setOpen((v) => !v)}
              aria-label="Menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      <MobileMenu open={open} menuPages={menuPages} />
    </>
  );
}

function DienstenDropdown({ menuPages, pathname }) {
  const [drop, setDrop] = useState(false);
  useEffect(() => {
    setDrop(false);
  }, [pathname]);
  return (
    <div
      className="relative"
      onMouseEnter={() => setDrop(true)}
      onMouseLeave={() => setDrop(false)}
    >
      <NavLink
        to="/diensten"
        className={({ isActive }) =>
          `flex items-center gap-1.5 transition-colors ${
            isActive || pathname.startsWith("/diensten")
              ? "text-bone"
              : "text-bone-muted hover:text-bone"
          }`
        }
        data-testid="nav-diensten"
      >
        Diensten
        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${drop ? "rotate-180" : ""}`} />
      </NavLink>
      {drop && (
          <div
            className="absolute left-0 top-full pt-4 w-72 animate-in fade-in slide-in-from-bottom-2 duration-200"
            data-testid="diensten-dropdown"
          >
            <div className="bg-white border border-black/10 shadow-xl shadow-space/10 rounded-lg p-2">
              <NavLink
                to="/diensten"
                className="block px-4 py-2.5 text-sm font-semibold text-bone hover:bg-ink-soft rounded-md transition-colors"
                data-testid="dropdown-alle-diensten"
              >
                Alle diensten
              </NavLink>
              <div className="my-1 h-px bg-black/5" />
              {menuPages.map((p) => (
                <NavLink
                  key={p.slug}
                  to={`/diensten/${p.slug}`}
                  className="block px-4 py-2.5 text-sm text-bone-muted hover:text-bone hover:bg-ink-soft rounded-md transition-colors"
                  data-testid={`dropdown-${p.slug}`}
                >
                  {p.menu_label || p.title}
                </NavLink>
              ))}
            </div>
          </div>
        )}
    </div>
  );
}

function MobileMenu({ open, menuPages }) {
  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 md:hidden bg-ink/95 backdrop-blur-2xl overflow-y-auto animate-in fade-in duration-200"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col justify-start min-h-full px-8 pt-28 pb-16 gap-5">
            <MobileLink to="/" label="Home" i={0} end />
            <div className={STAGGER} style={{ animationDelay: "60ms" }}>
              <NavLink
                to="/diensten"
                className="font-display text-4xl font-extrabold text-bone tracking-tighter"
              >
                Diensten
              </NavLink>
              <div className="mt-3 ml-1 flex flex-col gap-2.5 border-l border-black/10 pl-4">
                {menuPages.map((p) => (
                  <NavLink
                    key={p.slug}
                    to={`/diensten/${p.slug}`}
                    className="text-base text-bone-muted hover:text-bone transition-colors"
                    data-testid={`mobile-sub-${p.slug}`}
                  >
                    {p.menu_label || p.title}
                  </NavLink>
                ))}
              </div>
            </div>
            <MobileLink to="/over-mij" label="Over mij" i={2} />
            <MobileLink to="/cases" label="Cases" i={3} />
            <MobileLink to="/blog" label="Blog" i={4} />
            <div className={STAGGER} style={{ animationDelay: "300ms" }}>
              <NavLink
                to="/digitaliseringsscan"
                className="inline-flex items-center gap-2 font-display text-4xl font-extrabold text-bell tracking-tighter"
                data-testid="mobile-nav-ai-scan"
              >
                <Sparkles className="h-7 w-7" /> AI-scan
              </NavLink>
            </div>
            <MobileLink to="/contact" label="Contact" i={6} />
          </div>
        </div>
      )}
    </>
  );
}

const STAGGER = "animate-in fade-in slide-in-from-bottom-4 fill-mode-both duration-300";

function MobileLink({ to, label, i, end = false }) {
  return (
    <div className={STAGGER} style={{ animationDelay: `${i * 60}ms` }}>
      <NavLink
        to={to}
        end={end}
        className="font-display text-4xl font-extrabold text-bone tracking-tighter"
      >
        {label}
      </NavLink>
    </div>
  );
}
